import { useEffect, useState, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { X, Download, ZoomIn, ZoomOut, ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "../../lib/utils";

/**
 * Full-screen in-app image viewer with an optional swipe-through GALLERY.
 *
 * Pass either a single image (`src` + `filename`) or a gallery
 * (`images: [{ src, filename }]` + `startIndex`). With a gallery you get
 * prev/next arrows, ←/→ keys, touch swipe, and a "3 / 8" counter.
 * Click the backdrop / press Escape to close; click the image (or the zoom
 * button) to toggle fit ↔ actual size. Rendered through a portal on
 * document.body so no parent overflow clips it.
 */
export default function ImageLightbox({ images, startIndex = 0, src, filename, onClose }) {
  const imgs = (images && images.length) ? images : (src ? [{ src, filename }] : []);
  const many = imgs.length > 1;
  const [idx, setIdx] = useState(() => Math.min(Math.max(0, startIndex), Math.max(0, imgs.length - 1)));
  const [zoomed, setZoomed] = useState(false);
  const touchStartX = useRef(null);
  const cur = imgs[idx] || {};

  const go = useCallback((delta) => {
    if (imgs.length < 2) return;
    setZoomed(false);
    setIdx((i) => (i + delta + imgs.length) % imgs.length);
  }, [imgs.length]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft") go(-1);
      else if (e.key === "ArrowRight") go(1);
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", onKey); document.body.style.overflow = prev; };
  }, [onClose, go]);

  if (!cur.src) return null;
  const stop = (e) => e.stopPropagation();

  const onTouchStart = (e) => { touchStartX.current = e.touches?.[0]?.clientX ?? null; };
  const onTouchEnd = (e) => {
    if (touchStartX.current == null) return;
    const dx = (e.changedTouches?.[0]?.clientX ?? touchStartX.current) - touchStartX.current;
    if (Math.abs(dx) > 45) go(dx < 0 ? 1 : -1);
    touchStartX.current = null;
  };

  const NavBtn = ({ dir, onClick }) => (
    <button
      onClick={(e) => { stop(e); onClick(); }}
      className="absolute top-1/2 -translate-y-1/2 w-11 h-11 rounded-full flex items-center justify-center bg-white/10 hover:bg-white/25 text-white transition-colors z-10"
      style={dir === "prev" ? { left: 12 } : { right: 12 }}
      title={dir === "prev" ? "Previous (←)" : "Next (→)"}
      data-testid={`lightbox-${dir}`}
    >
      {dir === "prev" ? <ChevronLeft className="w-6 h-6" /> : <ChevronRight className="w-6 h-6" />}
    </button>
  );

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/85 backdrop-blur-sm"
      onClick={onClose}
      data-testid="chat-image-lightbox"
    >
      {/* Top control bar */}
      <div className="absolute top-0 inset-x-0 flex items-center justify-between px-4 py-3 bg-gradient-to-b from-black/60 to-transparent z-10" onClick={stop}>
        <div className="flex items-center gap-3 min-w-0">
          <span className="text-white/90 text-sm font-medium truncate max-w-[45vw]" title={cur.filename}>
            {cur.filename || "Image"}
          </span>
          {many && (
            <span className="text-white/60 text-xs font-medium shrink-0" data-testid="lightbox-counter">
              {idx + 1} / {imgs.length}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1.5">
          <button
            onClick={() => setZoomed((z) => !z)}
            className="w-9 h-9 rounded-full flex items-center justify-center bg-white/10 hover:bg-white/20 text-white transition-colors"
            title={zoomed ? "Fit to screen" : "Actual size"}
            data-testid="lightbox-zoom"
          >
            {zoomed ? <ZoomOut className="w-5 h-5" /> : <ZoomIn className="w-5 h-5" />}
          </button>
          <a
            href={cur.src}
            download={cur.filename || true}
            target="_blank"
            rel="noopener noreferrer"
            onClick={stop}
            className="w-9 h-9 rounded-full flex items-center justify-center bg-white/10 hover:bg-white/20 text-white transition-colors"
            title="Download"
            data-testid="lightbox-download"
          >
            <Download className="w-5 h-5" />
          </a>
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-full flex items-center justify-center bg-white/10 hover:bg-white/20 text-white transition-colors"
            title="Close (Esc)"
            data-testid="lightbox-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      {many && <NavBtn dir="prev" onClick={() => go(-1)} />}
      {many && <NavBtn dir="next" onClick={() => go(1)} />}

      {/* Image — scrollable when zoomed to actual size */}
      <div
        className="w-full h-full overflow-auto flex items-center justify-center p-4"
        onClick={onClose}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        <img
          key={cur.src}
          src={cur.src}
          alt={cur.filename || "attachment"}
          onClick={(e) => { stop(e); setZoomed((z) => !z); }}
          className={cn(
            "rounded-lg shadow-2xl transition-all duration-200",
            zoomed ? "max-w-none cursor-zoom-out" : "max-w-[94vw] max-h-[86vh] object-contain cursor-zoom-in"
          )}
          data-testid="lightbox-image"
        />
      </div>
    </div>,
    document.body
  );
}
